import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Alert, App, Button, Card, Col, Descriptions, Input, Modal, Progress, Row, Space, Spin, Statistic, Table, Tabs, Tag, Typography } from 'antd';
import { ArrowLeftOutlined, EditOutlined, EyeOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import recognitionService, { type ContractDetail, type ContractInput, type SchedulePreview } from '@/services/financeRecognition.service';
import DocumentUpload from '@/components/documents/DocumentUpload';
import DocumentList from '@/components/documents/DocumentList';
import { ApprovalPanel } from '@/components/approval/ApprovalPanel';
import { ENTITY_TYPE_RECOGNITION_CONTRACT } from '@/constants/approval-entity-types';
import {
  DAY_COUNT_LABEL,
  METHOD_LABEL,
  POSTING_MODE_LABEL,
  StatusTag,
  accountLabel,
  amount,
  apiErrorMessage,
  contractPath,
  humanize,
  jvPath,
  useRecognitionAccess,
} from './prepaidDisplay';
import { SchedulePreviewTable } from './PrepaidFormParts';
import { ActivityTab, AdjustMenu, AdjustmentsTab, JournalsTab, ScheduleTab, adjustPath } from './PrepaidContractTabs';

const { Title, Text } = Typography;

const DOCUMENT_ENTITY_TYPE = 'RecognitionContract';

const SOURCE_ROUTES: Record<string, string> = {
  DirectPurchaseInvoice: '/fin/direct-purchase-invoices/:id',
  SupplierInvoice: '/scm/procurement/supplier-invoices/:id',
};

const inclusiveDays = (from: string, to: string) => dayjs(to).diff(dayjs(from), 'day') + 1;

type Confirm = { kind: 'cancel' | 'close' | 'reject'; title: string; ok: string; required: boolean };

/** Rebuilds the draft input from the stored contract (used to preview / regenerate the schedule unchanged). */
function draftInput(c: ContractDetail): ContractInput {
  const opening = c.source_type === 'OPENING_BALANCE';
  return {
    category_id: c.category_id,
    description: c.description,
    transaction_currency: c.transaction_currency,
    exchange_rate: c.exchange_rate,
    recognition_method: c.recognition_method,
    day_count_method: c.day_count_method,
    partial_month_policy: c.partial_month_policy,
    posting_mode: c.posting_mode,
    prepaid_asset_account_id: c.prepaid_asset_account_id,
    expense_account_id: c.expense_account_id,
    allocations: c.allocations.map(({ id: _i, line_no: _l, ...a }) => a),
    ...(opening
      ? {
          opening: {
            original_amount: c.original_amount ?? c.transaction_amount,
            historically_recognized_amount: c.historically_recognized_amount ?? '0',
            migration_date: c.migration_date ?? c.recognition_start_date,
            original_service_start: c.original_service_start ?? c.service_start_date,
            original_service_end: c.original_service_end ?? c.service_end_date,
          },
        }
      : {
          transaction_amount: c.transaction_amount,
          recognizable_amount: c.recognizable_amount,
          service_start_date: c.service_start_date,
          service_end_date: c.service_end_date,
          recognition_start_date: c.recognition_start_date,
          recognition_end_date: c.recognition_end_date,
        }),
  };
}

const PrepaidContractDetailPage: React.FC = () => {
  const { t } = useTranslation('finance');
  const { t: tc } = useTranslation('common');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const access = useRecognitionAccess();
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') || 'overview';
  const [confirm, setConfirm] = useState<Confirm | null>(null);
  const [reason, setReason] = useState('');
  const [preview, setPreview] = useState<SchedulePreview | null>(null);

  const { data: c, isLoading, isError, error } = useQuery({
    queryKey: ['recognition', 'contract', id],
    queryFn: () => recognitionService.getContract(id!),
    enabled: Boolean(id),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['recognition'] });
  const action = useMutation({
    mutationFn: async (kind: 'submit' | 'approve' | 'activate' | 'cancel' | 'close' | 'reject') => {
      switch (kind) {
        case 'submit':
          return recognitionService.submitContract(id!);
        case 'approve':
          return recognitionService.approveContract(id!);
        case 'activate':
          return recognitionService.activateContract(id!);
        case 'cancel':
          return recognitionService.cancelContract(id!, reason.trim());
        case 'close':
          return recognitionService.closeContract(id!, reason.trim() || undefined);
        case 'reject':
          return recognitionService.rejectContract(id!, { reason: reason.trim() });
      }
    },
    onSuccess: (_r, kind) => {
      message.success(
        {
          submit: 'Submitted',
          approve: 'Approved',
          activate: 'Activated — recognition will follow the schedule',
          cancel: 'Cancelled',
          close: 'Closed',
          reject: 'Rejected and returned to draft',
        }[kind]
      );
      setConfirm(null);
      setReason('');
      invalidate();
    },
    onError: (e) => message.error(apiErrorMessage(e, 'Action failed')),
  });

  const previewMutation = useMutation({
    mutationFn: () => recognitionService.previewSchedule(draftInput(c!)),
    onSuccess: setPreview,
    onError: (e) => message.error(apiErrorMessage(e, 'Preview failed')),
  });
  const regenerate = useMutation({
    mutationFn: () => recognitionService.updateContract(id!, { ...draftInput(c!), version: c!.version }),
    onSuccess: () => {
      message.success(t('scheduleRegenerated'));
      setPreview(null);
      invalidate();
    },
    onError: (e) => message.error(apiErrorMessage(e, 'Regeneration failed')),
  });

  if (!access.canView) return <Alert type="warning" showIcon message={t('noAccessPrepaidContracts')} style={{ margin: 24 }} />;
  if (isLoading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }
  if (isError || !c) {
    return (
      <Alert
        style={{ margin: 24 }}
        type="error"
        showIcon
        message={apiErrorMessage(error, 'Prepaid contract not found')}
        action={<Button onClick={() => navigate('/fin/prepaid/contracts')}>{t('backToList')}</Button>}
      />
    );
  }

  const ccy = c.transaction_currency;
  const pct = Number(c.recognizable_amount) > 0 ? Math.min(100, Math.round((Number(c.recognized_amount) / Number(c.recognizable_amount)) * 1000) / 10) : 0;
  const isDraft = c.status === 'DRAFT';
  const pendingViaRuntime = c.status === 'PENDING_APPROVAL' && Boolean(c.approval_instance_id);
  const sourceRoute = c.source_document_type && c.source_document_id ? SOURCE_ROUTES[c.source_document_type]?.replace(':id', c.source_document_id) : undefined;
  const foreign = c.transaction_currency !== c.base_currency;

  const openConfirm = (kind: Confirm['kind']) =>
    setConfirm(
      {
        cancel: { kind, title: `Cancel ${c.recognition_number}`, ok: 'Cancel contract', required: true },
        close: { kind, title: `Close ${c.recognition_number}`, ok: 'Close contract', required: false },
        reject: { kind, title: `Reject ${c.recognition_number}`, ok: 'Reject', required: true },
      }[kind]
    );

  const headerActions = (
    <Space wrap>
      {isDraft && access.canEdit && (
        <Button icon={<EditOutlined />} onClick={() => navigate(`${contractPath(c.id)}/edit`)}>
          {tc('edit')}
        </Button>
      )}
      {isDraft && access.canSubmit && (
        <Button type="primary" loading={action.isPending && action.variables === 'submit'} onClick={() => action.mutate('submit')}>
          {tc('submit')}
        </Button>
      )}
      {c.status === 'PENDING_APPROVAL' && !pendingViaRuntime && access.canApprove && (
        <>
          <Button type="primary" loading={action.isPending && action.variables === 'approve'} onClick={() => action.mutate('approve')}>
            {tc('approve')}
          </Button>
          <Button danger onClick={() => openConfirm('reject')}>
            {tc('reject')}
          </Button>
        </>
      )}
      {c.status === 'APPROVED' && access.canActivate && (
        <Button type="primary" loading={action.isPending && action.variables === 'activate'} onClick={() => action.mutate('activate')}>
          {t('activate')}
        </Button>
      )}
      {['TERMINATED', 'FULLY_RECOGNIZED'].includes(c.status) && access.canActivate && <Button onClick={() => openConfirm('close')}>{t('closeAction')}</Button>}
      {['DRAFT', 'PENDING_APPROVAL', 'APPROVED'].includes(c.status) && access.canEdit && (
        <Button danger onClick={() => openConfirm('cancel')}>
          {tc('cancel')}
        </Button>
      )}
      <AdjustMenu contract={c} />
    </Space>
  );

  const overview = (
    <Descriptions bordered size="small" column={{ xs: 1, md: 2, xl: 3 }}>
      <Descriptions.Item label={t('recognitionNumber')}>{c.recognition_number}</Descriptions.Item>
      <Descriptions.Item label={tc('status')}>
        <StatusTag status={c.status} />
      </Descriptions.Item>
      <Descriptions.Item label={t('category')}>{c.category_name ?? '—'}</Descriptions.Item>
      <Descriptions.Item label={t('description')} span={2}>
        {c.description}
      </Descriptions.Item>
      <Descriptions.Item label={t('supplier')}>
        {c.supplier_id ? <Link to={`/scm/procurement/suppliers/${c.supplier_id}`}>{c.supplier_name}</Link> : '—'}
      </Descriptions.Item>
      <Descriptions.Item label={t('currency')}>{ccy}</Descriptions.Item>
      <Descriptions.Item label={t('originalAmount')}>{amount(c.transaction_amount, ccy)}</Descriptions.Item>
      <Descriptions.Item label={t('branch')}>{c.branch_name ?? '—'}</Descriptions.Item>
      <Descriptions.Item label={t('department')}>{accountLabel(c.department_code, c.department_name)}</Descriptions.Item>
      <Descriptions.Item label={t('sourceInvoice')}>
        {sourceRoute ? <Link to={sourceRoute}>{c.source_document_number}</Link> : c.supplier_invoice_number || humanize(c.source_type)}
      </Descriptions.Item>
      <Descriptions.Item label={t('invoiceDate')}>{c.invoice_date ?? '—'}</Descriptions.Item>
      <Descriptions.Item label={t('poReference')}>{c.purchase_order_reference ?? '—'}</Descriptions.Item>
      <Descriptions.Item label={t('contractReference')}>{c.contract_reference ?? '—'}</Descriptions.Item>
      <Descriptions.Item label={t('serviceStart')}>{c.service_start_date}</Descriptions.Item>
      <Descriptions.Item label={t('serviceEnd')}>{c.service_end_date}</Descriptions.Item>
      <Descriptions.Item label={t('recognitionMethod')}>{METHOD_LABEL[c.recognition_method] ?? humanize(c.recognition_method)}</Descriptions.Item>
      <Descriptions.Item label={t('postingMode')}>{POSTING_MODE_LABEL[c.posting_mode] ?? humanize(c.posting_mode)}</Descriptions.Item>
      <Descriptions.Item label={t('nextRecognition')}>
        {c.next_line ? `${c.next_line.accounting_period_key} on ${c.next_line.posting_date}: ${amount(c.next_line.planned_amount, ccy)}` : '—'}
      </Descriptions.Item>
      {c.status === 'SUSPENDED' && (
        <Descriptions.Item label={t('suspension')} span={3}>
          {c.suspension_start_date} → {c.suspension_end_date ?? 'open'} · {humanize(c.suspension_treatment)} · {c.suspension_reason}
        </Descriptions.Item>
      )}
      {c.termination_date && <Descriptions.Item label={t('terminationDate')}>{c.termination_date}</Descriptions.Item>}
      <Descriptions.Item label={t('notes')} span={3}>
        {c.notes || '—'}
      </Descriptions.Item>
    </Descriptions>
  );

  const totalDays = inclusiveDays(c.recognition_start_date, c.recognition_end_date);
  const recognition = (
    <Space direction="vertical" style={{ width: '100%' }}>
      <Descriptions bordered size="small" column={{ xs: 1, md: 2, xl: 3 }}>
        <Descriptions.Item label={t('recognitionStart')}>{c.recognition_start_date}</Descriptions.Item>
        <Descriptions.Item label={t('recognitionEnd')}>{c.recognition_end_date}</Descriptions.Item>
        <Descriptions.Item label={t('totalRecognitionDays')}>{totalDays}</Descriptions.Item>
        <Descriptions.Item label={t('recognitionMethod')}>{METHOD_LABEL[c.recognition_method] ?? humanize(c.recognition_method)}</Descriptions.Item>
        <Descriptions.Item label={t('dayCount')}>{DAY_COUNT_LABEL[c.day_count_method] ?? humanize(c.day_count_method)}</Descriptions.Item>
        <Descriptions.Item label={t('partialMonth')}>{humanize(c.partial_month_policy)}</Descriptions.Item>
        <Descriptions.Item label={t('recognisableAmount')}>{amount(c.recognizable_amount, ccy)}</Descriptions.Item>
        <Descriptions.Item label={t('recognisedAmount')}>{amount(c.recognized_amount, ccy)}</Descriptions.Item>
        <Descriptions.Item label={t('remainingAmount')}>{amount(c.remaining_amount, ccy)}</Descriptions.Item>
        <Descriptions.Item label={t('currentRevision')}>{c.current_revision}</Descriptions.Item>
        <Descriptions.Item label={t('refunded')}>{amount(c.refunded_amount, ccy)}</Descriptions.Item>
        <Descriptions.Item label={t('writtenOff')}>{amount(c.written_off_amount, ccy)}</Descriptions.Item>
        {c.source_type === 'OPENING_BALANCE' && (
          <>
            <Descriptions.Item label={t('originalPreMigration')}>{amount(c.original_amount, ccy)}</Descriptions.Item>
            <Descriptions.Item label={t('recognisedBeforeMigration')}>{amount(c.historically_recognized_amount, ccy)}</Descriptions.Item>
            <Descriptions.Item label={t('migrationDate')}>{c.migration_date}</Descriptions.Item>
          </>
        )}
      </Descriptions>
      <Table
        size="small"
        title={() => 'Schedule status summary'}
        rowKey="status"
        pagination={false}
        dataSource={c.line_status_summary}
        columns={[
          { title: t('lineStatus'), dataIndex: 'status', render: (s: string) => <StatusTag status={s} /> },
          { title: t('lines'), dataIndex: 'n', align: 'right', width: 90 },
          { title: `Amount (${ccy})`, dataIndex: 'amount', align: 'right', width: 160, render: (v: string) => amount(v) },
        ]}
      />
      <Space wrap>
        {isDraft && (
          <>
            <Button icon={<EyeOutlined />} loading={previewMutation.isPending} onClick={() => previewMutation.mutate()}>
              {t('previewSchedule')}
            </Button>
            {access.canEdit && (
              <Button loading={regenerate.isPending} onClick={() => regenerate.mutate()}>
                {t('regenerateSchedule')}
              </Button>
            )}
          </>
        )}
        {!isDraft && access.canAdjust('REVISION') && ['SCHEDULED', 'ACTIVE', 'SUSPENDED'].includes(c.status) && (
          <Button onClick={() => navigate(adjustPath(c.id, 'REVISION'))}>{t('reviseRegenerateFutureSchedule')}</Button>
        )}
        {!isDraft && <Text type="secondary">{t('postedLinesNeverRegenerated')}</Text>}
      </Space>
      {preview && <SchedulePreviewTable preview={preview} currency={ccy} />}
    </Space>
  );

  const accounting = (
    <Descriptions bordered size="small" column={{ xs: 1, md: 2, xl: 3 }}>
      <Descriptions.Item label={t('prepaidAssetAccount')}>
        {accountLabel(c.prepaid_account_code, c.prepaid_account_name)} {Number(c.prepaid_account_controlled) ? <Tag color="blue">{t('recognitionControlled')}</Tag> : null}
      </Descriptions.Item>
      <Descriptions.Item label={t('expenseAccount')}>{accountLabel(c.expense_account_code, c.expense_account_name)}</Descriptions.Item>
      <Descriptions.Item label={t('initialPosting')}>
        {c.initial_posting_mode === 'POST_ON_ACTIVATION' ? `On activation, credit ${accountLabel(c.clearing_account_code, c.clearing_account_name)}` : 'Already in GL'}
        {c.initial_journal_voucher_id && (
          <>
            {' '}
            · <Link to={jvPath(c.initial_journal_voucher_id)}>{c.initial_journal_voucher_number}</Link>
          </>
        )}
      </Descriptions.Item>
      <Descriptions.Item label={t('transactionCurrency')}>{c.transaction_currency}</Descriptions.Item>
      <Descriptions.Item label={t('baseCurrency')}>{c.base_currency}</Descriptions.Item>
      <Descriptions.Item label={t('exchangeRate')}>{foreign ? c.exchange_rate : t('oneBaseCurrency')}</Descriptions.Item>
      <Descriptions.Item label={t('recognisableBase')}>{amount(c.recognizable_base_amount, c.base_currency)}</Descriptions.Item>
      <Descriptions.Item label={t('recognisedBase')}>{amount(c.recognized_base_amount, c.base_currency)}</Descriptions.Item>
      <Descriptions.Item label={t('remainingBase')}>{amount(c.remaining_base_amount, c.base_currency)}</Descriptions.Item>
      <Descriptions.Item label={t('postingMode')}>{POSTING_MODE_LABEL[c.posting_mode] ?? humanize(c.posting_mode)}</Descriptions.Item>
      <Descriptions.Item label={t('postingGranularity')}>{c.posting_granularity ? humanize(c.posting_granularity) : 'Company policy'}</Descriptions.Item>
      <Descriptions.Item label={t('project')}>{c.project_name ?? '—'}</Descriptions.Item>
      <Descriptions.Item label={t('costCentre')}>{c.cost_center ?? '—'}</Descriptions.Item>
      <Descriptions.Item label={t('department')}>{accountLabel(c.department_code, c.department_name)}</Descriptions.Item>
      <Descriptions.Item label={t('branch')}>{c.branch_name ?? '—'}</Descriptions.Item>
    </Descriptions>
  );

  const allocation = (
    <Space direction="vertical" style={{ width: '100%' }}>
      {c.allocations.length === 0 ? (
        <Alert type="info" showIcon message={`No allocation: 100% to ${accountLabel(c.expense_account_code, c.expense_account_name)}.`} />
      ) : (
        <Table
          size="small"
          rowKey="id"
          pagination={false}
          dataSource={c.allocations}
          columns={[
            { title: '#', dataIndex: 'line_no', width: 50 },
            { title: t('basis'), dataIndex: 'allocation_basis', width: 130, render: (v: string) => humanize(v) },
            { title: t('percentage'), dataIndex: 'percentage', width: 110, align: 'right', render: (v: string | null) => (v == null ? '—' : `${Number(v)}%`) },
            { title: t('fixedAmount'), dataIndex: 'fixed_amount', width: 140, align: 'right', render: (v: string | null) => (v == null ? '—' : amount(v, ccy)) },
            { title: t('expenseAccount'), dataIndex: 'expense_account_id', render: (v: string | null) => (v ? v === c.expense_account_id ? accountLabel(c.expense_account_code, c.expense_account_name) : 'Override' : 'Contract default') },
            { title: t('costCentre'), dataIndex: 'cost_center', render: (v: string | null) => v ?? '—' },
            { title: t('project'), dataIndex: 'project_id', render: (v: string | null) => (v ? (v === c.project_id ? c.project_name : 'Assigned') : '—') },
            { title: t('effectiveFrom'), dataIndex: 'effective_from', width: 120, render: (v: string | null) => v ?? '—' },
            { title: t('effectiveTo'), dataIndex: 'effective_to', width: 120, render: (v: string | null) => v ?? '—' },
          ]}
        />
      )}
      <Space>
        {isDraft && access.canEdit && <Button onClick={() => navigate(`${contractPath(c.id)}/edit`)}>{t('editAllocation')}</Button>}
        {!isDraft && access.canAdjust('CHANGE_DIMENSIONS') && ['SCHEDULED', 'ACTIVE', 'SUSPENDED'].includes(c.status) && (
          <Button onClick={() => navigate(adjustPath(c.id, 'CHANGE_DIMENSIONS'))}>{t('changeAllocationFromDate')}</Button>
        )}
      </Space>
    </Space>
  );

  const source = (
    <Descriptions bordered size="small" column={{ xs: 1, md: 2 }}>
      <Descriptions.Item label={t('sourceType')}>{humanize(c.source_type)}</Descriptions.Item>
      <Descriptions.Item label={t('purchaseInvoice')}>
        {sourceRoute ? <Link to={sourceRoute}>{c.source_document_number}</Link> : c.source_document_number ?? '—'}
      </Descriptions.Item>
      <Descriptions.Item label={t('invoiceLine')}>{c.source_document_line_id ? t('linkedOneContractPerLine') : '—'}</Descriptions.Item>
      <Descriptions.Item label={t('supplierInvoiceNo')}>{c.supplier_invoice_number ?? '—'}</Descriptions.Item>
      <Descriptions.Item label={t('purchaseOrder')}>{c.purchase_order_reference ?? '—'}</Descriptions.Item>
      <Descriptions.Item label={t('contract')}>{c.contract_reference ?? '—'}</Descriptions.Item>
      <Descriptions.Item label={t('supplier')}>
        {c.supplier_id ? <Link to={`/scm/procurement/suppliers/${c.supplier_id}`}>{c.supplier_name}</Link> : '—'}
      </Descriptions.Item>
      <Descriptions.Item label={t('parentContract')}>
        {c.parent_contract_id ? <Link to={contractPath(c.parent_contract_id)}>{c.parent_recognition_number}</Link> : '—'}
      </Descriptions.Item>
      <Descriptions.Item label={t('additionsChildContracts')} span={2}>
        {c.child_contracts.length
          ? c.child_contracts.map((ch) => (
              <Link key={ch.id} to={contractPath(ch.id)} style={{ marginRight: 12 }}>
                {ch.recognition_number} ({amount(ch.recognizable_amount, ccy)}, {humanize(ch.status)})
              </Link>
            ))
          : '—'}
      </Descriptions.Item>
      <Descriptions.Item label={t('paymentsCreditNotesOther')} span={2}>
        Refunds, credit notes and other documents recorded on adjustments are listed in the Adjustments tab; attachments are in Documents.
      </Descriptions.Item>
    </Descriptions>
  );

  const documents = (
    <Space direction="vertical" style={{ width: '100%' }}>
      <Text type="secondary">{t('attachSupportingDocsHint')}</Text>
      {(access.canEdit || access.canCreate) && <DocumentUpload entityType={DOCUMENT_ENTITY_TYPE} entityId={c.id} />}
      <DocumentList entityType={DOCUMENT_ENTITY_TYPE} entityId={c.id} />
    </Space>
  );

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ marginBottom: 12 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/fin/prepaid/contracts')}>
          {t('contracts')}
        </Button>
      </Space>

      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} lg={10}>
            <Text type="secondary">{c.recognition_number}</Text>
            <Title level={4} style={{ margin: '2px 0 6px' }}>
              {c.description}
            </Title>
            <Space wrap>
              <StatusTag status={c.status} />
              {c.category_name && <Tag>{c.category_name}</Tag>}
              {c.source_type !== 'MANUAL' && <Tag color="blue">{humanize(c.source_type)}</Tag>}
              {c.pending_revision_count > 0 && <Tag color="gold">{c.pending_revision_count} adjustment(s) pending</Tag>}
            </Space>
          </Col>
          <Col xs={8} lg={3}>
            <Statistic title={t('original')} value={amount(c.recognizable_amount, ccy)} valueStyle={{ fontSize: 16 }} />
          </Col>
          <Col xs={8} lg={3}>
            <Statistic title={t('recognised')} value={amount(c.recognized_amount, ccy)} valueStyle={{ fontSize: 16 }} />
          </Col>
          <Col xs={8} lg={3}>
            <Statistic title={t('remaining')} value={amount(c.remaining_amount, ccy)} valueStyle={{ fontSize: 16 }} />
          </Col>
          <Col xs={24} lg={5}>
            <Progress percent={pct} size="small" />
            <Text type="secondary" style={{ fontSize: 12 }}>
              {c.service_start_date} → {c.service_end_date}
            </Text>
          </Col>
          <Col span={24}>{headerActions}</Col>
        </Row>
      </Card>

      {pendingViaRuntime && (
        <Card style={{ marginBottom: 16 }}>
          <ApprovalPanel
            entityType={ENTITY_TYPE_RECOGNITION_CONTRACT}
            entityId={c.id}
            onApproveViaDocument={async (remarks) => {
              await recognitionService.approveContract(c.id, remarks);
            }}
            onRejectViaDocument={async (payload) => {
              await recognitionService.rejectContract(c.id, payload);
            }}
            extraInvalidateQueryKeys={[['recognition']]}
          />
        </Card>
      )}

      <Card>
        <Tabs
          activeKey={tab}
          onChange={(k) => setParams({ tab: k })}
          destroyInactiveTabPane
          items={[
            { key: 'overview', label: t('overview'), children: overview },
            { key: 'recognition', label: t('recognition'), children: recognition },
            { key: 'schedule', label: t('schedule'), children: <ScheduleTab contract={c} /> },
            { key: 'accounting', label: t('accounting'), children: accounting },
            { key: 'allocation', label: t('allocation'), children: allocation },
            { key: 'source', label: t('sourceDocuments'), children: source },
            { key: 'adjustments', label: t('adjustments'), children: <AdjustmentsTab contract={c} /> },
            { key: 'journals', label: t('journalEntries'), children: <JournalsTab contract={c} /> },
            { key: 'documents', label: t('documents'), children: documents },
            { key: 'activity', label: t('activity'), children: <ActivityTab contract={c} /> },
          ]}
        />
      </Card>

      <Modal
        open={!!confirm}
        title={confirm?.title}
        okText={confirm?.ok}
        okButtonProps={{ danger: confirm?.kind !== 'close', disabled: Boolean(confirm?.required) && reason.trim().length < 3 }}
        confirmLoading={action.isPending}
        onCancel={() => {
          setConfirm(null);
          setReason('');
        }}
        onOk={() => confirm && action.mutate(confirm.kind)}
      >
        <Input.TextArea rows={3} placeholder={confirm?.required ? 'Reason (required)' : 'Reason (optional)'} value={reason} onChange={(e) => setReason(e.target.value)} />
      </Modal>
    </div>
  );
};

export default PrepaidContractDetailPage;
